import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Eye, Send, Image, Video, DollarSign } from 'lucide-react';
import axios from 'axios';
import CategoryRating from '../components/CategoryRating';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

import API from '../utils/api';

const CreatorDetail = () => {
  const { creatorId, slug } = useParams();
  const identifier = slug || creatorId;
  const { user } = useAuth();
  const [creator, setCreator] = useState(null);
  const [comments, setComments] = useState([]);
  const [userRating, setUserRating] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchCreator = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/creators/${identifier}`);
      setCreator(response.data);
    } catch (error) {
      console.error('Error fetching creator:', error);
    } finally {
      setLoading(false);
    }
  }, [identifier]);

  const fetchComments = useCallback(async (creatorData) => {
    if (!creatorData) return;
    try {
      const response = await axios.get(`${API}/comments/${creatorData.creator_id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  }, []);

  const fetchUserRating = useCallback(async (creatorData) => {
    if (!creatorData) return;
    try {
      const response = await axios.get(`${API}/ratings/${creatorData.creator_id}`, {
        withCredentials: true
      });
      if (response.data) {
        setUserRating(response.data);
      }
    } catch (error) {
      console.error('Error fetching user rating:', error);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCreator();
  }, [fetchCreator]);
  
  useEffect(() => {
    if (creator) {
      fetchComments(creator);
      if (user) {
        fetchUserRating(creator);
      }
    }
  }, [creator, user, fetchComments, fetchUserRating]);

  const handleRatingSubmit = async (values) => {
    if (!user || !creator) return;
    
    try {
      await axios.post(`${API}/ratings`, {
        creator_id: creator.creator_id,
        ...values
      }, { withCredentials: true });
      
      setUserRating(values);
      fetchCreator();
      toast.success('Avaliacao enviada!');
    } catch (error) {
      console.error('Error rating:', error);
      toast.error('Erro ao enviar avaliacao');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user || !newComment.trim() || !creator) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/comments`, {
        creator_id: creator.creator_id,
        content: newComment.trim()
      }, { withCredentials: true });
      
      setNewComment('');
      toast.success('Comentario enviado! Aguardando aprovacao.');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Erro ao enviar comentario');
    } finally {
      setSubmitting(false);
    }
  };

  const creatorKeywords = useMemo(() => {
    if (!creator) return [];
    return [creator.name, `${creator.name} privacy`, `${creator.name} fotos`];
  }, [creator]);

  const creatorSchema = useMemo(() => {
    if (!creator) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": creator.name,
      "description": creator.bio,
      "image": creator.profile_image,
      "url": `https://privacyfotos.com.br/${creator.slug || creator.creator_id}`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": creator.avg_rating,
        "reviewCount": creator.rating_count,
        "bestRating": 5,
        "worstRating": 1
      }
    };
  }, [creator]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-12 h-12 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-16">
        <p className="text-slate-600 text-lg">Criador nao encontrado</p>
        <Link to="/" className="mt-4 text-[#FF5A00] hover:underline">Voltar ao inicio</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20 pb-24 md:pb-16">
      <SEO 
        title={creator.name}
        description={`${creator.name} - ${creator.bio?.substring(0, 150)}... Veja avaliacoes e notas de assinantes.`}
        image={creator.profile_image}
        url={`/${creator.slug || creator.creator_id}`}
        type="profile"
        keywords={creatorKeywords}
        schema={creatorSchema}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#FF5A00] mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
          Voltar
        </Link>

        {/* Creator Header */}
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm" data-testid="creator-detail">
          {/* Cover Image */}
          {creator.cover_image && (
            <div className="h-48 sm:h-64 overflow-hidden">
              <img 
                src={creator.cover_image} 
                alt={`${creator.name} cover`}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Profile Image */}
              <div className={`${creator.cover_image ? '-mt-20' : ''} flex-shrink-0`}>
                <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white shadow-lg">
                  <img 
                    src={creator.profile_image || '/favicon.svg'}
                    alt={creator.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900">
                  {creator.name}
                </h1>
                
                {/* Content Stats */}
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  {creator.photos_count > 0 && (
                    <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full">
                      <Image className="h-4 w-4 text-[#FF5A00]" />
                      <span className="text-sm font-semibold text-slate-700">{creator.photos_count} Fotos</span>
                    </div>
                  )}
                  {creator.videos_count > 0 && (
                    <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full">
                      <Video className="h-4 w-4 text-[#FF5A00]" />
                      <span className="text-sm font-semibold text-slate-700">{creator.videos_count} Videos</span>
                    </div>
                  )}
                  {creator.price && (
                    <div className="flex items-center gap-1.5 bg-[#FFF0E5] px-3 py-1.5 rounded-full">
                      <DollarSign className="h-4 w-4 text-[#FF5A00]" />
                      <span className="text-sm font-semibold text-[#FF5A00]">{creator.price}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-[#FFF0E5] px-3 py-1.5 rounded-full">
                      <span className="text-[#FF5A00] font-bold text-lg">{creator.avg_rating.toFixed(1)}</span>
                      <span className="text-[#FF5A00]/60 text-sm">/10</span>
                    </div>
                    <span className="text-slate-600 font-medium">
                      ({creator.rating_count} avaliacoes)
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1 text-slate-500">
                    <Eye className="h-4 w-4" />
                    {creator.views.toLocaleString()} visualizacoes
                  </div>
                </div>

                <p className="mt-4 text-slate-600 leading-relaxed">
                  {creator.bio}
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <a
              href={creator.affiliate_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#FF5A00] text-white font-bold tracking-wide rounded-full py-4 px-8 shadow-[0_8px_16px_rgba(255,90,0,0.2)] hover:-translate-y-1 hover:shadow-[0_12px_24px_rgba(255,90,0,0.3)] transition-all cta-button"
              data-testid="creator-detail-cta"
            >
              Ver Conteudo Completo
              <ExternalLink className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Category Ratings Breakdown */}
        {creator.rating_count > 0 && (
          <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-outfit text-xl font-bold text-slate-900 mb-4">
              Avaliacoes da Comunidade
            </h2>
            <CategoryRating readonly ratings={creator} />
          </div>
        )}

        {/* User Rating Section */}
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-outfit text-xl font-bold text-slate-900 mb-4">
            {userRating ? 'Sua Avaliacao' : 'Avaliar'}
          </h2>
          
          {user ? (
            <CategoryRating 
              ratings={userRating || {}} 
              onSubmit={handleRatingSubmit}
            />
          ) : (
            <p className="text-slate-500">
              <button 
                onClick={() => window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`}
                className="text-[#FF5A00] hover:underline"
              >
                Entre
              </button>
              {' '}para avaliar este criador
            </p>
          )}
        </div>

        {/* Comments Section */}
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-outfit text-xl font-bold text-slate-900 mb-4">
            Comentarios ({comments.length})
          </h2>

          {/* Comment Form */}
          {user ? (
            <form onSubmit={handleSubmitComment} className="mb-6">
              <div className="flex gap-3">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-[#FF5A00] text-white">
                    {user.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Escreva um comentario..."
                    className="resize-none"
                    rows={3}
                    data-testid="comment-input"
                  />
                  <button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="mt-2 bg-[#FF5A00] text-white font-semibold px-4 py-2 rounded-full hover:bg-[#E04F00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="submit-comment"
                  >
                    <Send className="h-4 w-4" />
                    Enviar
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <p className="text-slate-500 mb-6">
              <button 
                onClick={() => window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`}
                className="text-[#FF5A00] hover:underline"
              >
                Entre
              </button>
              {' '}para deixar um comentario
            </p>
          )}

          {/* Comments List */}
          {comments.length > 0 ? (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div 
                  key={comment.comment_id} 
                  className="flex gap-3 p-4 bg-slate-50 rounded-xl"
                  data-testid={`comment-${comment.comment_id}`}
                >
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarImage src={comment.user_picture} alt={comment.user_name} />
                    <AvatarFallback className="bg-slate-300 text-slate-600">
                      {comment.user_name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{comment.user_name}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(comment.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <p className="mt-1 text-slate-600">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              Nenhum comentario ainda. Seja o primeiro!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorDetail;
